import { Badge, Box, Flex, Text } from '@chakra-ui/react';
import { JobListItem } from '@texas/api/endpoints/jobRecordsApi';
import { Locale } from '@texas/i18n/types';
import {
  dateFromNow,
  DateFormat,
  formatDate,
} from '@texas/utils/helpers/dateHelper';
import { createColumnHelper } from '@tanstack/react-table';
import { TFunction } from 'i18next';
import { AngularNavLink } from '../shared/AngularNavLink';
import {
  getJobStateLabel,
  getJobTypeLabel,
  getStateColors,
} from './jobPresentation';

export function jobsColumns(t: TFunction) {
  const columnHelper = createColumnHelper<JobListItem>();

  return [
    columnHelper.accessor('id', {
      cell: (info) => (
        <AngularNavLink
          navobj={{ jobId: info.getValue() }}
          textDecoration="none"
          to="jobs.detail"
          variant="unstyled"
        >
          <Flex direction="column" gap={1}>
            <Text fontWeight="semibold">
              {getJobTypeLabel(info.row.original.type, t)}
            </Text>
            <Text color="entityDirectory.mutedText" fontSize="12px">
              #{info.getValue()}
            </Text>
          </Flex>
        </AngularNavLink>
      ),
      header: t('jobs.columns.job'),
      size: 280,
      minSize: 240,
    }),
    columnHelper.accessor('entityCount', {
      cell: (info) => {
        const job = info.row.original;
        const scope = info.getValue()
          ? t('jobs.entitiesCount', { count: info.getValue() })
          : job.latestMessage;

        return (
          <Flex direction="column" gap={1}>
            <Text fontWeight="medium" noOfLines={1}>
              {scope}
            </Text>
            <Text
              color="entityDirectory.mutedText"
              fontSize="12px"
              noOfLines={1}
            >
              {job.latestMessage}
            </Text>
          </Flex>
        );
      },
      header: t('jobs.columns.scope'),
      size: 260,
      minSize: 220,
      meta: { toggleable: true, defaultVisible: true },
    }),
    columnHelper.accessor('userName', {
      cell: (info) => info.getValue(),
      header: t('jobs.columns.requestedBy'),
      size: 190,
      minSize: 160,
      meta: { toggleable: true, defaultVisible: true },
    }),
    columnHelper.accessor('created', {
      cell: (info) => (
        <Flex direction="column" gap={1}>
          <Text>
            {formatDate(Locale.En, info.getValue(), DateFormat.Datetime)}
          </Text>
          <Text color="entityDirectory.mutedText" fontSize="12px">
            {dateFromNow(Locale.En, info.getValue())}
          </Text>
        </Flex>
      ),
      header: t('jobs.columns.started'),
      size: 210,
      minSize: 180,
      meta: { toggleable: true, defaultVisible: true },
    }),
    columnHelper.accessor('percentComplete', {
      cell: (info) => {
        const colors = getStateColors(info.row.original.state);
        return (
          <Flex align="center" gap={3}>
            <Box
              bg="entityDirectory.progressTrack"
              flex={1}
              h="5px"
              maxW="90px"
              minW="56px"
            >
              <Box bg={colors.bar} h="5px" w={`${info.getValue()}%`} />
            </Box>
            <Text fontSize="12px" fontWeight="semibold">
              {info.getValue()}%
            </Text>
          </Flex>
        );
      },
      header: t('jobs.columns.progress'),
      size: 180,
      minSize: 150,
      meta: { toggleable: true, defaultVisible: true },
    }),
    columnHelper.accessor('state', {
      cell: (info) => {
        const colors = getStateColors(info.getValue());
        return (
          <Badge variant={colors.badgeVariant}>
            {getJobStateLabel(info.getValue(), t).toUpperCase()}
          </Badge>
        );
      },
      header: t('jobs.columns.status'),
      size: 130,
      minSize: 115,
    }),
  ];
}
